import { createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { tenantPaymentReminderRepository } from '~/repositories/settings/tenantPaymentReminderRepository';
import type { Accessor } from 'solid-js';
import type {
  TenantPaymentReminderSettings,
  TenantPaymentReminderSettingsCompanyPortalTypedSettingsWithOverridesDocument,
} from '~/swagger/settings/Api';

interface TenantPaymentReminderContextValue {
  tenantPaymentReminderSettings: Accessor<TenantPaymentReminderSettingsCompanyPortalTypedSettingsWithOverridesDocument>;
  isLoading: Accessor<boolean>;
  isSaving: Accessor<boolean>;
  getTenantPaymentReminderSettings: () => Promise<void>;
  updateTenantPaymentReminderSettings: (data: TenantPaymentReminderSettings) => Promise<void>;
  updateTenantPaymentReminderSettingsByLevel: (level: string, levelId: string, data: TenantPaymentReminderSettings) => Promise<void>;
  deleteTenantPaymentReminderSettingsByLevel: (level: string, levelId: string) => Promise<void>;
  error: Accessor<Error | undefined>;
}

export const [TenantPaymentReminderProvider, useTenantPaymentReminder] = createMagicDoorContext<TenantPaymentReminderContextValue>(
  'TenantPaymentReminder',
  () => {
    const repo = new tenantPaymentReminderRepository();

    const [isLoading, setIsLoading] = createSignal<boolean>(false);
    const [isSaving, setIsSaving] = createSignal<boolean>(false);
    const [tenantPaymentReminderSettings, setTenantPaymentReminderSettings] =
      createSignal<TenantPaymentReminderSettingsCompanyPortalTypedSettingsWithOverridesDocument>(
        {} as TenantPaymentReminderSettingsCompanyPortalTypedSettingsWithOverridesDocument
      );

    const [error, setError] = createSignal<Error | undefined>(undefined);

    const getTenantPaymentReminderSettings = async () => {
      setIsLoading(true);
      try {
        const result = await repo.getTenantPaymentReminderAll();
        setTenantPaymentReminderSettings(result);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    const runWrite = async (write: () => Promise<unknown>) => {
      setIsSaving(true);
      try {
        await write();
        await getTenantPaymentReminderSettings();
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      } finally {
        setIsSaving(false);
      }
    };

    const updateTenantPaymentReminderSettings = (data: TenantPaymentReminderSettings) =>
      runWrite(() => repo.updateTenantPaymentReminder(data));

    const updateTenantPaymentReminderSettingsByLevel = (level: string, levelId: string, data: TenantPaymentReminderSettings) =>
      runWrite(() => repo.updateTenantPaymentReminderByLevel(level, levelId, data));

    const deleteTenantPaymentReminderSettingsByLevel = (level: string, levelId: string) =>
      runWrite(() => repo.deleteTenantPaymentReminderByLevel(level, levelId));

    return {
      error,
      tenantPaymentReminderSettings,
      isLoading,
      isSaving,
      getTenantPaymentReminderSettings,
      updateTenantPaymentReminderSettings,
      updateTenantPaymentReminderSettingsByLevel,
      deleteTenantPaymentReminderSettingsByLevel,
    };
  }
);
